import Box from '@mui/material/Box';
import { type JSX, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { FileManagementArea, FileManagementAreaRestriction, useFileManagementArea } from 'Components/Files/FileManagementArea';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { AI_DOC_GENERATOR_DESCRIPTION, AI_DOC_GENERATOR_INPUT_FILE, AI_DOC_GENERATOR_REGULATION, AI_DOC_GENERATOR_TITLE, AI_DOC_GENERATOR_TYPE } from 'Config/Tooltips';
import { AiDocGeneratorCreateRequest } from 'Models/ArtificialIntelligence';
import { Regulation } from 'Models/ComplianceRequirements';
import { ValidationError } from 'Models/ErrorTypes';
import { FileToBeUploaded } from 'Models/Files';
import { GovernanceType, GovernanceValuesSelectOptions } from 'Models/Governance';

import styles from './AiDocGeneratorCreateForm.module.css';

export interface AiDocGeneratorCreateFormProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    regulations: Regulation[];
    setFailureMessage: (message?: string) => void;
    setSuccessMessage: (message?: string) => void;
}

export const AiDocGeneratorCreateForm = (props: AiDocGeneratorCreateFormProps): JSX.Element => {
    // Form fields.
    const [description, setDescription] = useState<string>('');
    const [submitRequestWithManagedFiles, fileManagementHookValues] = useFileManagementArea(props.documentApi, [], FileManagementAreaRestriction.UP_TO_ONE_FILE);
    const [selectedRegulation, setSelectedRegulation] = useState<Regulation>();
    const [title, setTitle] = useState<string>('');
    const [type, setType] = useState<GovernanceType | undefined>(GovernanceType.POLICY);
    // Form state.
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    /** Handler for when the submit button is clicked. Includes handling file submission, if applicable. */
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        props.setFailureMessage(undefined);
        props.setSuccessMessage(undefined);
        setIsSubmitting(true);

        try {
            await submitRequestWithManagedFiles(async (fileUpdates) => await submitRequest(fileUpdates.new_files.length > 0 ? fileUpdates.new_files[0] : undefined));
            props.setSuccessMessage(`A ${type} is being generated. Refresh the listing page in a few minutes to see the new document.`);
            setIsSubmitted(true);
        } catch (error) {
            props.setFailureMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    /** Submit the form to the back-end API to create a new AI-generated document. */
    const submitRequest = async (newFile: FileToBeUploaded | undefined) => {
        if (title === undefined || title.trim().length === 0) {
            throw new ValidationError('Title is required.');
        }

        if (type === undefined || type.trim().length === 0) {
            throw new ValidationError('Type is required.');
        }

        if (description === undefined || description.trim().length === 0) {
            throw new ValidationError('Description is required.');
        }

        const request: AiDocGeneratorCreateRequest = {
            description: description,
            input_file_id: newFile?.file_id,
            input_filename: newFile?.filename,
            regulation: selectedRegulation,
            title: title,
            type: type,
        };
        await props.artificialIntelligenceApi.aiDocGeneratorCreate(request);
    };

    const disableForm = isSubmitting || isSubmitted;

    return (
        <Box component="form" noValidate onSubmit={handleSubmit}>
            <FormFieldText formFieldId="title" formFieldLabel="Title" required tooltip={AI_DOC_GENERATOR_TITLE} handleChange={(event: React.FormEvent<HTMLInputElement>) => setTitle(event.currentTarget.value)} value={title} disabled={disableForm} />
            <FormFieldSelect formFieldId="type" formFieldLabel="Type" required tooltip={AI_DOC_GENERATOR_TYPE} handleChange={(value: ChangeEventType) => setType(value as GovernanceType)} options={GovernanceValuesSelectOptions} selectedOption={type} disabled={disableForm} notClearable />
            <FormFieldTextArea formFieldId="description" formFieldLabel="Description" required tooltip={AI_DOC_GENERATOR_DESCRIPTION} handleChange={(event: React.FormEvent<HTMLInputElement>) => setDescription(event.currentTarget.value)} value={description} disabled={disableForm} />
            <FileManagementArea disabled={disableForm} documentApi={props.documentApi} fileManagementHookValues={fileManagementHookValues} fileDragAndDropFormFieldLabel="Reference Document" tooltip={AI_DOC_GENERATOR_INPUT_FILE} />
            <FormFieldSelect formFieldId="regulation" formFieldLabel="Regulation" tooltip={AI_DOC_GENERATOR_REGULATION} handleChange={(value: ChangeEventType) => setSelectedRegulation(props.regulations.find((regulation) => regulation.name === value) as Regulation | undefined)} options={props.regulations.map((regulation) => ({ label: regulation.name, value: regulation.name }))} selectedOption={selectedRegulation?.name} disabled={disableForm} />
            <div className={styles.disclaimer}>
                <GenerativeAiDisclaimer />
            </div>
            <div className={styles.submitButton}>
                <Button variant="submit" isLoading={isSubmitting} loadingText="Submitting..." disabled={disableForm}>
                    Submit
                </Button>
            </div>
        </Box>
    );
};
